import { CardType, CategoryType, defaultCard, generateDefaultCategory } from 'types/deck'

import { alpha } from 'redux/deck/helpers/sorting'

export type SectionHeader = 'none' | 'primaryCategory' | 'generic' | 'cardType'

function stringifyCategory(categoryName: string, categoryMap: Record<string, CategoryType>): string {
  const category = categoryMap[categoryName]

  let response = `${categoryName}`

  if (!category) return response

  if (category?.isPremier) response += '{top}'
  if (!category?.includedInDeck) response += '{noDeck}'
  if (!category?.includedInPrice) response += '{noPrice}'

  return response
}

function sortByCategory(
  cards: Array<CardType>,
  categoryMap: Record<string, CategoryType>,
  sectionHeader: SectionHeader,
): Array<CardType> {
  return cards.sort((a, b) => {
    const aCategory: CategoryType | undefined = categoryMap[a.categories[0]]
    const bCategory: CategoryType | undefined = categoryMap[b.categories[0]]

    if (aCategory?.isPremier && !bCategory?.isPremier) return -1
    if (!aCategory?.isPremier && bCategory?.isPremier) return 1

    if (aCategory?.name === 'Sideboard' && bCategory?.name !== 'Sideboard') return 1
    if (aCategory?.name !== 'Sideboard' && bCategory?.name === 'Sideboard') return -1

    if (aCategory?.includedInDeck && !bCategory?.includedInDeck) return -1
    if (!aCategory?.includedInDeck && bCategory?.includedInDeck) return 1

    const aGroupByName = sectionHeader === 'cardType' ? a.typeCategory : a.categories[0]
    const bGroupByName = sectionHeader === 'cardType' ? b.typeCategory : b.categories[0]

    if (aGroupByName === bGroupByName) return alpha(a, b)

    return aGroupByName < bGroupByName ? -1 : 1
  })
}

function getCardHeader(
  card: CardType,
  categoryMap: Record<string, CategoryType>,
  sectionHeaderType: SectionHeader,
): string {
  if (sectionHeaderType === 'none') return ''

  const categoryName = card.categories[0]
  const primaryCategory = categoryMap[categoryName]

  // If the card is in the sideboard or is a premier category, we can just return the category name regardless of the section header type
  if (primaryCategory.name === 'Sideboard' || primaryCategory.isPremier) return primaryCategory.name

  if (sectionHeaderType === 'primaryCategory') return categoryName
  if (sectionHeaderType === 'cardType') return card.typeCategory

  // Safely assuming we're checking the 'generic' section header type now

  const category = categoryMap[categoryName]

  if (!category) return ''
  if (category.isPremier && category.includedInDeck) return 'Commander'
  if (category.includedInDeck && category.name === 'Sideboard') return 'Sideboard'
  if (!category.includedInDeck) return 'Maybeboard'

  return 'Mainboard'
}

export function buildTextFile(
  cards: Array<CardType> = [],
  categoryMap: Record<string, CategoryType>,
  includeQuantityX = true,
  frontNameOnly = false,
  includeSetCode = true,
  includeCollectorNumber = true,
  includeFoil = true,
  includeCategories = true,
  includeColorTags = true,
  sectionHeader: SectionHeader = 'none',
): string {
  const appendedHeaders = new Array<string>() // Goes unused if sectionHeader is 'none'
  const orderedCards = sectionHeader === 'none' ? cards.sort(alpha) : sortByCategory(cards, categoryMap, sectionHeader)

  return orderedCards.reduce((outputString, card) => {
    if (sectionHeader !== 'none') {
      const cardHeader = getCardHeader(card, categoryMap, sectionHeader)
      const isNewHeader = !appendedHeaders.includes(cardHeader)

      if (isNewHeader) {
        // If we're not on the first header, add a newline
        if (outputString !== '') outputString += '\n'

        outputString += `${cardHeader}\n`

        appendedHeaders.push(cardHeader)
      }
    }

    let newLine: string = `${card.qty}`

    newLine += includeQuantityX ? 'x' : ''
    newLine += ` ${frontNameOnly ? (card.front?.name ?? card.name) : card.name}`

    if (includeSetCode && !!card.setCode) newLine += ` (${card.setCode})`
    if (includeCollectorNumber && includeSetCode) newLine += ` ${card.collectorNumber}` // We only support collector number if set code is also included

    if (includeFoil && card.modifier === 'Foil') newLine += ' *F*'
    if (includeFoil && card.modifier === 'Etched') newLine += ' *E*'

    if (includeCategories)
      newLine += ` [${card.categories.map(category => stringifyCategory(category, categoryMap)).join(',')}]`

    if (includeColorTags && card.colorLabel.name) newLine += ` ^${card.colorLabel.name},${card.colorLabel.color}^`

    if (!!card.customCard) newLine += ` #CustomCard`

    newLine += '\n'

    return outputString + newLine
  }, '')
}

export function generateExampleLine(
  includeQuantityX = true,
  frontNameOnly = false,
  includeSetCode = true,
  includeCollectorNumber = true,
  includeFoil = true,
  includeCategories = true,
  includeColorTags = true,
  sectionHeader: SectionHeader = 'none',
) {
  const exampleCategories = { 'Example category': generateDefaultCategory('Example category') }
  const exampleCards: CardType[] = [
    {
      ...defaultCard,
      qty: 1,
      name: 'Example Card',
      setCode: 'set',
      collectorNumber: '123',
      categories: ['Example category'],
      types: ['Creature'],
      typeCategory: 'Creature',
      modifier: 'Foil',
      colorLabel: { name: 'Buy', color: '#0066ff' },
    },
  ]

  const exampleWithNoHeaders = buildTextFile(
    exampleCards,
    exampleCategories,
    includeQuantityX,
    frontNameOnly,
    includeSetCode,
    includeCollectorNumber,
    includeFoil,
    includeCategories,
    includeColorTags,
    'none',
  )

  const exampleWithHeaders = buildTextFile(
    exampleCards,
    exampleCategories,
    includeQuantityX,
    frontNameOnly,
    includeSetCode,
    includeCollectorNumber,
    includeFoil,
    includeCategories,
    includeColorTags,
    sectionHeader,
  )

  return { exampleWithNoHeaders, exampleWithHeaders }
}
